import { useEffect, useRef, useState } from "react";
import PatientService from "../service/PatientService";
import { originalMemberId } from "../helper/bookingFor";

const nameOf = (p) => [p && p.firstName, p && p.lastName].filter(Boolean).join(" ").trim();

/**
 * The member an appointment is BOOKED for, while "Update booking for" has
 * switched its record to someone else. Two uses:
 *   · the "Original appointment" pill (Figma 3820:3089) — `name`;
 *   · the call — `callingNumber`. A switch deliberately does not move the call
 *     (product, 2026-09-11): the number in the ECHO call header, and the one
 *     "Call patient" dials, stay this member's.
 *
 * Not switched: { name: "", callingNumber: undefined } — callers use the record.
 * Switched: `name` falls back to "PID n" until the record is read (the pill must
 * never vanish while the switch is in force); `callingNumber` is null until then
 * (and stays null if it can't be read), then { countryCode, phoneno }.
 *
 * Read from that member's record because the appointment row carries no clean
 * member name (the server appends the member's FIRST name to the holder's in
 * `patientName`).
 */
export const useOriginalBooking = (appointment) => {
  const service = useRef(new PatientService()).current;
  const id = originalMemberId(appointment);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    setRecord(null);
    if (!id) return undefined;
    let cancelled = false;
    service.fetchPatient(
      id,
      (p) => {
        if (!cancelled && p && p.id) setRecord(p);
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [id, service]);

  if (!id) return { id: null, name: "", callingNumber: undefined };
  return {
    id,
    name: (record && nameOf(record)) || `PID ${id}`,
    callingNumber: record ? { countryCode: record.countryCode, phoneno: record.phoneno } : null,
  };
};

/** Just the pill's name (the Rx screen has no call). */
const useOriginalBookingName = (appointment) => useOriginalBooking(appointment).name;

export default useOriginalBookingName;
